/**
 * Types for the two file formats animo-editor defines: project.json and
 * scene / prefab files. Everything else in a project (asset packs, animation
 * JSON, Tiled maps) is a format Phaser or Tiled already owns.
 *
 * Every field on a scene object is optional unless noted; an omitted field
 * takes Phaser's default.
 */
import type { ClipsRef } from './clips.js';
import type { Connection } from './connections.js';

/** Plugin data, keyed by plugin id (for example `mygame.health`). */
export type PluginData = Record<string, unknown>;

/**
 * Scripts attached to an object, keyed by script path relative to
 * project.json's folder (such as `src/scripts/Patrol.ts`), with the values of
 * the fields each script exposes. Scripts run in key order. A prefab
 * placement can override single values, or remove a script with `null`.
 */
export type ScriptRefs = Record<string, Record<string, unknown> | null>;

/** Fields every display object has. */
export interface DisplayFields {
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  depth?: number;
  alpha?: number;
  visible?: boolean;
  scrollFactorX?: number;
  scrollFactorY?: number;
  /** A Phaser blend mode, by number or by name such as `ADD`. */
  blendMode?: number | string;
  /** Shaded by the scene's lights (Phaser's `setLighting`). Needs `lights.enabled` on the scene. */
  lit?: boolean;
  /** Phaser filters on the object, run in order. On a container they cover its children. */
  filters?: FilterEntry[];
}

/** A colour as an RGB number, such as 0xff0000. */
type Rgb = number;

interface FilterBase {
  /** False keeps the filter's settings but turns it off. */
  enabled?: boolean;
}

export interface GlowFilter extends FilterBase {
  type: 'glow';
  color?: Rgb;
  outerStrength?: number;
  innerStrength?: number;
  /** Draws only the glow, not the object. */
  knockout?: boolean;
}

export interface ShadowFilter extends FilterBase {
  type: 'shadow';
  /** Offset of the light that casts the shadow; the shadow falls the other way. */
  x?: number;
  y?: number;
  decay?: number;
  power?: number;
  color?: Rgb;
  intensity?: number;
}

export interface BlurFilter extends FilterBase {
  type: 'blur';
  /** 0 low, 1 medium, 2 high. */
  quality?: number;
  x?: number;
  y?: number;
  strength?: number;
  steps?: number;
}

/** The colour matrix looks Phaser has presets for. */
export const COLOR_MATRIX_LOOKS = [
  'greyscale', 'sepia', 'blackWhite', 'negative', 'brightness', 'contrast', 'saturate', 'desaturate', 'hue',
  'night', 'lsd', 'brown', 'vintage', 'kodachrome', 'technicolor', 'polaroid',
] as const;
export type ColorMatrixLook = (typeof COLOR_MATRIX_LOOKS)[number];

export interface ColorMatrixFilter extends FilterBase {
  type: 'colorMatrix';
  look?: ColorMatrixLook;
  /** How strong the look is. Its meaning depends on the look: degrees for hue, 0 to 1 for most others. */
  amount?: number;
}

export interface VignetteFilter extends FilterBase {
  type: 'vignette';
  /** Centre, from 0 to 1 across the object. */
  x?: number;
  y?: number;
  radius?: number;
  strength?: number;
  color?: Rgb;
}

export interface PixelateFilter extends FilterBase {
  type: 'pixelate';
  amount?: number;
}

export interface MaskFilter extends FilterBase {
  type: 'mask';
  /** Id of the object whose shape masks this one, in the same file. */
  object?: string;
  /** Or a texture key whose alpha masks this one. */
  texture?: string;
  invert?: boolean;
}

export interface WipeFilter extends FilterBase {
  type: 'wipe';
  /** Softness of the wipe's edge, from 0 to 1. */
  width?: number;
  direction?: 'left' | 'right' | 'up' | 'down';
  /** Reveal the object instead of wiping it away. */
  reveal?: boolean;
  /** How far the wipe has gone, from 0 to 1. */
  progress?: number;
}

/** A soft ring of light that sweeps out from the object's middle every so often. Sizes are in the object's pixels. */
export interface PingFilter extends FilterBase {
  type: 'ping';
  color?: Rgb;
  /** How much the ring brightens, from 0 to 1. */
  strength?: number;
  width?: number;
  softness?: number;
  /** Pixels a second. */
  speed?: number;
  /** Seconds from one ping to the next. */
  every?: number;
  /** Seconds into the cycle the first ping waits. */
  delay?: number;
}

/** Scanlines and a slight colour fringe, as on an old monitor. */
export interface CrtFilter extends FilterBase {
  type: 'crt';
  /** Scanlines from top to bottom. */
  lines?: number;
  /** Object heights a second the scanlines drift down. */
  scroll?: number;
  /** How much shows over the picture, from 0 to 1. */
  amount?: number;
  /** Pixels the colour fringe sits off the picture. */
  shift?: number;
}

/**
 * A custom shader: a `.frag` file in the project (see shader-source.ts) run
 * over the object like the other filters.
 */
export interface ShaderFilter extends FilterBase {
  type: 'shader';
  /** Project path of the shader file, such as `shaders/water.frag`. */
  shader: string;
  /**
   * Values for the shader's uniforms, by name, where they differ from the
   * shader's defaults: numbers, true or false, a texture key for a sampler2D,
   * and a list of numbers or an RGB colour number for vectors.
   */
  uniforms?: Record<string, number | boolean | string | number[]>;
  /** Pixels of room around the picture the shader may draw into, such as an outline's. */
  padding?: number;
}

export type FilterEntry =
  | GlowFilter
  | ShadowFilter
  | BlurFilter
  | ColorMatrixFilter
  | VignetteFilter
  | PixelateFilter
  | MaskFilter
  | WipeFilter
  | PingFilter
  | CrtFilter
  | ShaderFilter;

export type FilterType = FilterEntry['type'];

export interface BaseObject extends DisplayFields {
  /** Stable id, unique within its file. Never changes on rename or move. */
  id: string;
  type: string;
  name?: string;
  scripts?: ScriptRefs;
  /** The clips file this object plays, and the clip it starts with. */
  clips?: ClipsRef;
  /** This object's events wired to things other objects do (see connections.ts). */
  connections?: Connection[];
  /** Namespaced plugin data. `animo.editor` is reserved for editor-only state. */
  data?: PluginData;
}

export interface TextureRef {
  key: string;
  /** A frame name for atlases, or a frame index for sprite sheets. */
  frame?: string | number;
}

export interface OriginFields {
  originX?: number;
  originY?: number;
}

export interface ImageObject extends BaseObject, OriginFields {
  type: 'image';
  texture?: TextureRef;
  flipX?: boolean;
  flipY?: boolean;
  /** RGB colour as a number, such as 0xff0000. */
  tint?: number;
}

/**
 * An SVG drawn as shapes, so it stays sharp at any zoom: the texture is an
 * asset pack `svg` entry, which an `image` would show as pixels made once at
 * load. Its size is the SVG's own. Flat fills and strokes draw; filters,
 * masks, embedded images and text don't, and a gradient draws as one colour.
 */
export interface VectorObject extends BaseObject, OriginFields {
  type: 'vector';
  /** An asset pack `svg` entry. A frame is ignored. */
  texture?: TextureRef;
  /** The SVG's colours swapped for others, by `#rrggbb` as the SVG has it: a colour, or a theme colour's name. */
  colors?: Record<string, string>;
  /** RGB colour as a number, multiplied in, as on an image. */
  tint?: number;
}

export interface SpriteAnimationRef {
  key: string;
  autoplay?: boolean;
}

export interface SpriteObject extends Omit<ImageObject, 'type'> {
  type: 'sprite';
  animation?: SpriteAnimationRef;
}

/** Phaser's text style. Only the common keys are listed; others pass through. */
export interface TextStyle {
  fontFamily?: string;
  fontSize?: string | number;
  fontStyle?: string;
  color?: string;
  align?: string;
  wordWrap?: { width?: number; useAdvancedWrap?: boolean };
  [key: string]: unknown;
}

export interface TextObject extends BaseObject, OriginFields {
  type: 'text';
  text?: string;
  /** A text style from the project theme. `style` keys override it. */
  themeStyle?: string;
  style?: TextStyle;
}

/**
 * Text drawn from a bitmap font (a BMFont `.fnt` or `.xml` with its page
 * image, loaded by an asset pack `bitmapFont` entry): crisp pixel fonts and
 * lettering the artist baked outlines or shadows into.
 */
export interface BitmapTextObject extends BaseObject, OriginFields {
  type: 'bitmapText';
  /** The pack key of the bitmap font. */
  font?: string;
  text?: string;
  /** Pixel size. The font's own size when omitted. */
  fontSize?: number;
  align?: 'left' | 'center' | 'right';
  letterSpacing?: number;
  lineSpacing?: number;
  /** Wraps lines longer than this many pixels. 0 or omitted: no wrapping. */
  maxWidth?: number;
  tint?: number;
}

/**
 * A texture cut into nine pieces whose corners keep their size as it
 * stretches: panels, buttons and bar frames. Width and height are its real
 * size, not a scale. Omitted slice widths are 8. With top and bottom both 0
 * it is a three-slice, as in Phaser: it stretches sideways only.
 */
export interface NineSliceObject extends BaseObject, OriginFields {
  type: 'nineSlice';
  texture?: TextureRef;
  width?: number;
  height?: number;
  leftWidth?: number;
  rightWidth?: number;
  topHeight?: number;
  bottomHeight?: number;
  tint?: number;
}

export interface ZoneObject extends BaseObject, OriginFields {
  type: 'zone';
  width?: number;
  height?: number;
}

/** A texture that repeats to fill its size, and can scroll. */
export interface TileSpriteObject extends BaseObject, OriginFields {
  type: 'tileSprite';
  texture?: TextureRef;
  width?: number;
  height?: number;
  flipX?: boolean;
  flipY?: boolean;
  tint?: number;
  /** Offset of the repeating texture. */
  tilePositionX?: number;
  tilePositionY?: number;
  tileScaleX?: number;
  tileScaleY?: number;
  /** How fast the texture scrolls while the game runs, in pixels per second. */
  scrollSpeedX?: number;
  scrollSpeedY?: number;
}

/** A number picked at random between min and max for each particle. */
export interface ParticleRange {
  min: number;
  max: number;
}

/** A value that goes from start to end over each particle's life. */
export interface ParticleTween {
  start: number;
  end: number;
}

/** Where particles appear, relative to the emitter. */
export type ParticleZone =
  | { shape: 'point' }
  | { shape: 'box'; x?: number; y?: number; width: number; height: number; edge?: boolean }
  | { shape: 'circle'; x?: number; y?: number; radius: number; edge?: boolean }
  | { shape: 'line'; x1: number; y1: number; x2: number; y2: number };

/**
 * A particle emitter. Fields are Phaser's emitter settings under friendlier
 * names; omitted ones take Phaser's defaults.
 */
export interface ParticlesObject extends BaseObject {
  type: 'particles';
  texture?: TextureRef;
  /** Frames to pick from at random. Overrides `texture.frame`. */
  frames?: (string | number)[];
  /** Fire all at once (`explode`) instead of a steady stream. */
  burst?: boolean;
  /** Starts when the scene starts. For a burst, fires once. Default true. */
  emitting?: boolean;
  /** Ms between emissions. */
  frequency?: number;
  /** Particles per emission, or per burst. */
  quantity?: number;
  /** Most particles alive at once. 0 for no limit. */
  maxAlive?: number;
  /** Stops emitting after this many ms. */
  stopAfter?: number;
  /** Ms each particle lives. */
  lifespan?: number | ParticleRange;
  speed?: number | ParticleRange;
  /** Direction particles move in, in degrees: 0 points right, 90 down. */
  direction?: number | ParticleRange;
  gravityX?: number;
  gravityY?: number;
  rotate?: number | ParticleRange;
  scale?: number | ParticleTween;
  /** Each particle's alpha. The emitter's own `alpha` fades all of them. */
  particleAlpha?: number | ParticleTween;
  /** Tint from start to end colour (RGB numbers). */
  color?: Rgb | { start: Rgb; end: Rgb };
  /** Phaser ease name for scale, alpha and colour, such as `Quad.easeOut`. */
  ease?: string;
  zone?: ParticleZone;
}

/** A point light. It lights objects with `lit` when the scene has lights on. */
export interface LightObject extends BaseObject {
  type: 'light';
  color?: Rgb;
  intensity?: number;
  radius?: number;
  /** Height above the scene, which changes how normal maps catch the light. */
  z?: number;
}

export interface VideoObject extends BaseObject, OriginFields {
  type: 'video';
  /** The video's key in an asset pack. */
  key?: string;
  /** Starts playing when the scene starts. Default true. */
  autoplay?: boolean;
  loop?: boolean;
  /** Default true, since browsers block sound until the player clicks. */
  muted?: boolean;
  /** Playback speed; 1 is normal. */
  playbackRate?: number;
  flipX?: boolean;
  flipY?: boolean;
  tint?: number;
}

export interface ContainerObject extends BaseObject {
  type: 'container';
  /** Draw order is array order: Phaser never sorts container children by depth. */
  children?: SceneObject[];
}

export interface TilemapLayerObject extends BaseObject {
  type: 'tilemapLayer';
  /** An id from the `tilemaps` list of the file it is in: the scene's, or its prefab's. */
  map: string;
  /** The Tiled layer name, with any group prefix, such as `Ground/Base`. */
  layer: string;
}

/**
 * Overrides for objects inside a placed prefab, keyed by id path relative to
 * the placement (`p3`, or `n7/p2` inside a nested prefab).
 */
export type PrefabOverrides = Record<string, Record<string, unknown>>;

export interface PrefabPlacement extends BaseObject {
  type: 'prefab';
  /** Path of the prefab file, relative to the project root. */
  prefab: string;
  overrides?: PrefabOverrides;
}

/** An object whose type a plugin registered, such as `mygame.enemy`. */
export interface PluginObject extends BaseObject {
  [field: string]: unknown;
}

export type CoreObject =
  | ImageObject
  | VectorObject
  | SpriteObject
  | TextObject
  | BitmapTextObject
  | NineSliceObject
  | ZoneObject
  | TileSpriteObject
  | ParticlesObject
  | LightObject
  | VideoObject
  | ContainerObject
  | TilemapLayerObject
  | PrefabPlacement;

export type SceneObject = CoreObject | PluginObject;

export interface SceneTilemapRef {
  /** Id the scene's tilemapLayer entries refer to. */
  id: string;
  /** Path of the Tiled JSON file, relative to the project root. */
  file: string;
}

/** The scene's main camera, applied when the game builds the scene. The editor's own camera ignores it. */
export interface SceneCamera {
  /** CSS colour, such as `#15120f`. */
  backgroundColor?: string;
  zoom?: number;
  /** Id of the object the camera follows. */
  follow?: string;
  /** Follow smoothing from 0 to 1; 1 snaps. */
  lerp?: number;
  /** Where the camera may look: the scene's tilemaps, or a box in world pixels. */
  bounds?: 'tilemaps' | { x: number; y: number; width: number; height: number };
  roundPixels?: boolean;
  /** Filters over everything the camera shows. */
  filters?: FilterEntry[];
}

export interface SceneLights {
  /** Turns on Phaser's light manager for the scene. */
  enabled?: boolean;
  /** Ambient colour, as an RGB number. */
  ambient?: Rgb;
}

export interface SceneFile {
  formatVersion: number;
  /** The Phaser scene key, passed to SceneLoader.build. */
  key: string;
  camera?: SceneCamera;
  lights?: SceneLights;
  /** Asset pack files to load before building. */
  preload?: string[];
  /** Animation files to load before building. */
  animations?: string[];
  tilemaps?: SceneTilemapRef[];
  /** Music and ambience the scene starts, by sound name. */
  sound?: import('./sound.js').SceneSound;
  children?: SceneObject[];
  data?: PluginData;
}

export interface PrefabFile {
  formatVersion: number;
  /**
   * Tiled maps the prefab's tile layers use, such as a room's. Ids are the
   * prefab's own: every placement gets its own copy of each map, so two
   * placements of a room don't share tiles at runtime.
   */
  tilemaps?: SceneTilemapRef[];
  root: SceneObject;
}

export interface ProjectFile {
  formatVersion: number;
  name?: string;
  /**
   * Folder (relative to project.json) that the game serves at its base URL,
   * such as `public`. Every path inside project files is relative to it.
   */
  root?: string;
  resolution?: { width: number; height: number };
  /** Scene file paths, relative to `root`. */
  scenes?: string[];
  /**
   * Plugin entry modules, in the order their field plugins run. Resolved like
   * imports from project.json's folder. They must have no side effects on import.
   */
  plugins?: string[];
  /** Per-plugin project settings, keyed by plugin id. */
  settings?: PluginData;
  /** Path of the theme file, relative to `root`. Default `ui/theme.json`. */
  theme?: string;
  /** Path of the sounds file, relative to `root`. Default `audio/sounds.json`. */
  sounds?: string;
  /** Couch Games: the game it uploads to, by its name in Couch Games URLs. */
  couch?: {
    slug?: string;
    [key: string]: unknown;
  };
  /** Named actions and each player's keys and gamepad buttons for them. */
  input?: import('./input.js').InputMap;
  /** What uploads put in a build: unused media files are left out unless kept. */
  build?: import('./unused.js').BuildSettings;
  /** Editor-only settings the runtime ignores. */
  editor?: {
    grid?: { width: number; height: number };
    snap?: boolean;
    [key: string]: unknown;
  };
}
